import * as _ from 'lodash';
import {useEffect, useState, useMemo} from 'react';
import {LineStyle} from '../MediaCard';
import * as MediaImage from './mediaImage';
import * as Color from '../../util/colors';
import * as CG from './cg/graph';
import * as Op from './cg/ops';
import * as CGReact from './cg/cgreact';

export interface ClassSetState {
  classes: {
    [classID: string]: {
      color: string;
      name: string;
    };
  };
}

export interface ClassSetControls {
  [classSetID: string]: ClassSetState;
}

export interface OverlayControls {
  [overlayID: string]: OverlayState;
}

export interface OverlayClassState {
  opacity: number;
  disabled: boolean;
}

export interface BaseOverlayState {
  type: ControlType;
  name: string;
  disabled: boolean;
  classSearch: string;
  classSetID: string;
  classStates: {
    [classID: string]: OverlayClassState;
  };
}

export interface BoxControlState extends BaseOverlayState {
  type: 'box';
  lineStyle: LineStyle;
}

export interface MaskControlState extends BaseOverlayState {
  type: 'mask';
}

export type OverlayState = BoxControlState | MaskControlState;
export type ControlType = OverlayState['type'];

export type UpdateControl = <T extends BaseOverlayState>(
  newControl: Partial<T>
) => void;

export function useControlHelpers(
  control: BaseOverlayState,
  updateControl: UpdateControl
) {
  return {
    toggleControlVisibility: () => updateControl({disabled: !control.disabled}),
  };
}

function createBaseControls(
  name: string,
  classSetID: string,
  classSet: ClassSetState
): Omit<BaseOverlayState, 'type'> {
  const classStates = Object.fromEntries(
    Object.keys(classSet.classes).map(classID => [
      classID,
      {opacity: 1, disabled: false},
    ])
  );
  return {
    name,
    classSetID,
    classStates,
    classSearch: '',
    disabled: false,
  };
}

export function createMaskControls(
  ...args: Parameters<typeof createBaseControls>
) {
  return {...createBaseControls(...args), type: 'mask'};
}

export function createBoxControls(
  ...args: Parameters<typeof createBaseControls>
): BoxControlState {
  return {
    ...createBaseControls(...args),
    type: 'box',
    lineStyle: 'line',
  };
}

interface LoadedWBImage {
  loadedFrom: CG.Node;
  image: MediaImage.WBImage | null;
}

/**
 * Adds path property to loaded images.
 * @note Adds warning to array if path doesn't exist.
 */
const getClassFilePaths = (
  loadedImages: LoadedWBImage[],
  options?: {skip?: boolean}
) => {
  return options?.skip
    ? []
    : loadedImages.map(li => {
        const contents = li.image?.classes;
        if (contents == null) {
          return null;
        }
        return {loadedFrom: li.loadedFrom, path: contents.path};
      });
};

/**
 * Given a list of wandb image objects, which may contain overlays (boxes
 * and masks) make sure classSets and overlayControls contain the controls
 * we need for interacting with those overlays.
 */

export const useWBImageControls = (
  loadedImages: LoadedWBImage[],
  maskControls: OverlayControls | undefined,
  classSets: ClassSetControls | undefined,
  setMaskControls: (controlId: string, control: OverlayState) => void,
  setClassSet: (classSetId: string, classSet: ClassSetState) => void,
  skip: boolean
): {
  loading: boolean;
  controlIDs: string[][];
  maskControlsIDs: string[][];
  boxControlsIDs: string[][];
} => {
  const filePath = useMemo(() => {
    const classFilePaths = getClassFilePaths(loadedImages, {
      skip,
    });
    return classFilePaths[0]!;
  }, [loadedImages, skip]);
  const classFileContentsNode = useMemo(
    () =>
      filePath != null
        ? Op.opFileContents({
            file: Op.opArtifactFile({
              artifact: filePath.loadedFrom as any,
              path: Op.constString(filePath.path),
            }),
          })
        : null,
    [filePath]
  );
  const classFileContentsQuery = CGReact.useNodeValue(
    classFileContentsNode == null
      ? Op.constNodeUnsafe(null, null)
      : (classFileContentsNode as any)
  );
  const loading = skip || classFileContentsQuery.loading;

  const [controlIDs, setControlsIDs] = useState<string[][]>([]);

  useEffect(() => {
    if (loading) {
      return;
    }
    try {
      const nextControlsIDs = loadedImages.map((loadedImage, index) => {
        if (loadedImage.image == null) {
          return [];
        }

        const classFile = classFileContentsQuery;

        const classSetID = loadedImage.image.classes?.digest;
        if (classSetID == null) {
          return [];
        }

        let classSet = classSets?.[classSetID]!;
        if (classSet == null) {
          // Watch out, we're assuming user-input is valid here, parsing straight
          // from JSON into type data.
          // TODO: fix me with warning system
          let parsed: MediaImage.ClassSet = {
            type: 'class-set',
            class_set: [],
          };

          try {
            parsed = JSON.parse(classFile.result!);
          } catch {
            // pass
          }

          classSet = {
            classes: _.fromPairs(
              parsed.class_set.map(classInfo => [
                classInfo.id,
                {
                  color:
                    classInfo.color ||
                    Color.colorN(classInfo.id, Color.ROBIN16),
                  name: classInfo.name,
                },
              ])
            ),
          };
          setClassSet(classSetID, classSet);
        }

        const setDefaultControlsState = (
          type: ControlType,
          maskControlsID: string,
          maskName: string
        ) => {
          if (maskControls?.[maskControlsID] != null) {
            return;
          }

          const createFn =
            type === 'box' ? createBoxControls : createMaskControls;
          const controls = createFn(
            maskName,
            classSetID,
            classSet
          ) as OverlayState;
          setMaskControls(maskControlsID, controls);
        };

        const createControls = (type: ControlType, maskName: string) => {
          const controlsID = `${type}-${index}-${classSetID}-${maskName}`;
          setDefaultControlsState(type, controlsID, maskName);
          return controlsID;
        };

        const {boxes, masks} = loadedImage.image;
        const boxKeys = Object.keys(boxes ?? {});
        const maskKeys = Object.keys(masks ?? {});

        const imageBoundingBoxControlsIDs: string[] = boxKeys.map(k =>
          createControls('box', k)
        );
        const imageMaskControlsIDs: string[] = maskKeys.map(k =>
          createControls('mask', k)
        );

        return [...imageBoundingBoxControlsIDs, ...imageMaskControlsIDs];
      });
      setControlsIDs(nextControlsIDs);
    } catch (e) {
      console.log('IMAGE CONTROLS ERROR', e);
      return;
    }
  }, [
    classFileContentsQuery,
    classSets,
    loadedImages,
    loading,
    maskControls,
    setClassSet,
    setMaskControls,
  ]);

  const maskControlsIDs = controlIDs.map(mc =>
    mc.filter(id => id.startsWith('mask-'))
  );
  const boxControlsIDs = controlIDs.map(mc =>
    mc.filter(id => id.startsWith('box-'))
  );

  // We ensure that output length always matches input length, but its
  // possible to get glitches where old controls are returned for a given
  // input. We can use something like useParallelAsyncMap in files.ts to
  // fix this.
  if (loading || controlIDs.length !== loadedImages.length) {
    return {
      loading,
      controlIDs: loadedImages.map(li => []),
      maskControlsIDs: loadedImages.map(li => []),
      boxControlsIDs: loadedImages.map(li => []),
    };
  }

  return {
    loading,
    controlIDs,
    maskControlsIDs,
    boxControlsIDs,
  };
};
